import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { MediaTypeEnum } from '../../../Basic/enums/media-type.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AttributeModel } from '../../..//Misc/models/attribute.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ResizeDirectionEnum } from '../../../Forms/components/TextareaInput/textarea-input.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { Mention } from '../../../Mentions/models/mention';
import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { mentionAttributeTypesEnum } from '../../../../Monitor/Shared/enums/mention-attribute-types.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { ChangeTimeZonePipe } from '../../../Misc/pipes/ChangeTimeZone/change-time-zone.pipe';

@Component({
    selector: 'agpr-tveyes-archive-dialog',
    templateUrl: 'tveyes-archive-dialog.component.html',
    styleUrls: ['tveyes-archive-dialog.component.scss'],
    standalone: false
})
export class TVEyesArchiveDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() mention: Mention;
    @Input() iFrameURL: string;

    attributes: AttributeModel[] = [];
    highlightKeywords: KeywordHighlightModel[] = [];

    private subscriptions: Subscription[] = [];
    private preferences: PreferencesModel;

    readonly id = 'tveyesArchiveDialog';
    readonly styleEnum = AgprComponentStyleEnum;
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly resizeDirectionEnum = ResizeDirectionEnum;
    readonly mediaTypeEnum = MediaTypeEnum;

    constructor(
        public coreDialogService: CoreDialogService,
        private currentUserStateService: CurrentUserStateService,
        private changeTimeZonePipe: ChangeTimeZonePipe
    ) {
        super(coreDialogService, 'TVEyesArchive');
    }

    ngOnInit() {

        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                prefs => {
                    this.preferences = prefs;
                    this.attributes = this.generateMentionAttributes(this.mention, this.preferences);
                }
            ),
        );

        this.highlightKeywords = this.getKeywords();
    }


    close() {
        this.closeDialog();
    }

    private getKeywords(): KeywordHighlightModel[] {
        if (!this.mention.contexualKeywordIndices) {
            return [];
        }

        const keywords: KeywordHighlightModel[] = [];
        for (const {characterStartPoint, characterEndPoint} of this.mention.contexualKeywordIndices) {
            const keyword = this.mention.body.slice(characterStartPoint, characterEndPoint);
            if (keyword) {
                keywords.push({
                    keywords: [keyword],
                    caseSensitive: false
                });
            }
        }
        return keywords;
    }

    private generateMentionAttributes(mention: Mention, preferences: PreferencesModel): AttributeModel[] {

        const format = preferences.getDateFormatString(true, true, true);

        const attributes: AttributeModel[] = [
            {
                id: mentionAttributeTypesEnum.outlet,
                label: 'Mention.editDialog.outlet.label',
                value: this.mention.publication?.name,
                type: attributeTypesEnum.simple,
                icon: 'fa-kit fa-agpr-outlet-solid'
            },
            {
                id: mentionAttributeTypesEnum.publishedDate,
                label: 'publishedDate',
                value: this.changeTimeZonePipe.transform(this.mention.datePublished, preferences.timeZone, format),
                type: attributeTypesEnum.date,
                icon: 'fa-solid fa-calendar-days'
            },
            {
                id: mentionAttributeTypesEnum.audienceReach,
                label: 'Mention.details.audienceReach',
                value: this.mention.audienceReach,
                type: attributeTypesEnum.audienceReach,
                isSimilarWeb: this.mention.isAudienceFromSimilarWeb,
                icon: 'fa-solid fa-arrows-to-eye'
            },
            {
                id: mentionAttributeTypesEnum.adValueUsd,
                label: 'Mention.details.ave',
                value: this.mention.adValueUsd,
                type: attributeTypesEnum.adValueUsd,
                icon: 'fa-solid fa-circle-dollar',
                infoTooltip: 'Mention.details.aveTooltip'
            }
        ];

        if (mention.isTvEyes()) {
            const url = mention.getTVEyesDisplayUrl();

            if (url) {
                attributes.push({
                    id: mentionAttributeTypesEnum.url,
                    label: 'Mention.details.url',
                    value: url,
                    type: attributeTypesEnum.url,
                    url: url,
                    icon: 'fa-solid fa-link',
                    showCopyIcon: true,
                    copyValue: url,
                    copyToolTip: 'Mention.details.url',
                } as AttributeModel);
            }
        }


        return attributes;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
